/**
 * Create Folder Action
 * Creates a new subfolder within a folder
 */

import { Datum } from "../datastructure";
import { ActionDefinition, ActionContext, toApiPath } from "./types";
import { uploadFile } from "@/components/common/FileUploadV3";
import { isReadOnlyDatum } from "../specialFolders";

/**
 * Creates a new folder by uploading a .empty placeholder file
 */
export async function createFolder(
  parentDatum: Datum,
  folderName: string,
  context: ActionContext
): Promise<void> {
  if (parentDatum.type !== "folder") {
    console.error("createFolder: Parent must be a folder");
    return;
  }

  if (!folderName || folderName.trim() === "") {
    context.showToast?.("Folder name cannot be empty", "error");
    return;
  }

  // Check for duplicate folder names
  if (parentDatum.children) {
    const existingFolder = parentDatum.children.find(
      (child) => child.type === "folder" && child.name === folderName
    );
    if (existingFolder) {
      context.showToast?.(`Folder "${folderName}" already exists`, "error");
      return;
    }
  }

  // Build path as array - never join with "/" to preserve "/" in folder names
  const parentPath = toApiPath(parentDatum.path, context.filetree);
  const newFolderPath = [...parentPath, folderName];

  // Create an empty file to represent the folder
  const emptyBlob = new Blob([""], { type: "text/plain" });
  const emptyFile = new File([emptyBlob], ".empty", { type: "text/plain" });

  try {
    const driveId = parentDatum.payload?.driveId || context.driveId;

    await uploadFile({
      baseDirectoryPath: newFolderPath,
      file: emptyFile,
      batchSize: 1,
      asyncUpload: true,
      useFilesystem: true,
      driveId,
    });

    // Optimistically add the folder to the tree
    if (context.filetree) {
      const uiPath = [...parentDatum.path, folderName];
      context.filetree.ensureFoldersExist(uiPath, driveId || null);
      context.filetree.rebuildLookup();
    }
    context.refreshTree();
    context.showToast?.(`Created folder "${folderName}"`, "success");
  } catch (error) {
    console.error("createFolder: Failed to create folder", error);
    context.showToast?.("Failed to create folder", "error");
  }
}

/**
 * Action definition for context menu
 * Note: This action requires user input (folder name), so the execute function
 * will need to be called with the folder name after prompting the user
 */
export const createFolderAction: ActionDefinition = {
  config: {
    action: "create-folder",
    label: "Create New Folder",
  },
  isApplicable: (datum, context) =>
    datum.type === "folder" && !isReadOnlyDatum(datum, context),
  execute: async (datum, context) => {
    // This is a placeholder - actual implementation needs UI for input
    // The caller should prompt for the folder name and call createFolder directly
    const folderName = window.prompt("Enter folder name:");
    if (folderName) {
      await createFolder(datum, folderName, context);
    }
  },
};
